import { Injectable } from '@angular/core';
import { AnimalIdReconcilable, type AnimalIdRemap, ApiService, SynchronizationService } from '@homeleite/arch';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { map, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { base64ToBlob } from '../../../../common-utils/src/lib/utils/file.utils';

@Injectable({ providedIn: 'root' })
export class AnimalImageService implements AnimalIdReconcilable {
    constructor(
        private apiService: ApiService,
        private dbService: NgxIndexedDBService,
        private readonly synchronizationService: SynchronizationService
    ) {
        this.synchronizationService.registerAnimalIdReconciler(this);
    }

    getImageBy(animalId: string): Observable<void> {
        return this.apiService.root
            .path('animals')
            .path(animalId)
            .path('picture')
            .getBlob()
            .pipe(
                switchMap((imageBlob) => {
                    const imageRecord = {
                        animalId: animalId,
                        imageData: imageBlob,
                        timestamp: new Date().toISOString()
                    };
                    return this.dbService.update('animalImages', imageRecord);
                }),
                map(() => undefined),
                catchError((error) => {
                    console.error(`Falha ao sincronizar imagem para o animal ${animalId}:`, error);

                    return of(undefined);
                })
            );
    }

    public saveBase64Image(animalId: string, base64ImageUrl: string): Observable<void> {
        try {
            const imageBlob = base64ToBlob(base64ImageUrl);

            const imageRecord = {
                animalId: animalId,
                imageData: imageBlob,
                timestamp: new Date().toISOString()
            };

            return this.dbService.update('animalImages', imageRecord).pipe(
                map(() => undefined),
                catchError((error) => {
                    console.error(`Falha ao salvar imagem base64 no DB para o animal ${animalId}:`, error);
                    return of(undefined);
                })
            );
        } catch (error) {
            console.error(`Falha ao converter imagem base64 para o animal ${animalId}:`, error);
            return of(undefined);
        }
    }

    public reconcileAnimalId({ localAnimalId, returnedAnimalId, returnedAnimal }: AnimalIdRemap): Observable<void> {
        if (returnedAnimal.imageId) {
            return this.dbService.deleteByKey('animalImages', localAnimalId).pipe(
                switchMap(() => this.getImageBy(returnedAnimalId))
            );
        }

        return this.dbService.getByKey<{ animalId: string }>('animalImages', localAnimalId).pipe(
            switchMap((image) =>
                image
                    ? this.dbService
                          .update('animalImages', { ...image, animalId: returnedAnimalId })
                          .pipe(switchMap(() => this.dbService.deleteByKey('animalImages', localAnimalId)))
                    : of(undefined)
            ),
            map(() => undefined)
        );
    }
}
